import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import toast from "react-hot-toast";
import { CustomDialog } from "../../../../components/customDialog/CustomDialog";
import { RenderInputByTypeMobile } from "../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { GET_ALL_USUARIO_SOLICITANTE } from '../../../../data/graphQL/queries/GestionPrecioMasivo/getAllUsuarioSolicitante';
import { adaptAllUsuarioSolicitanteArray } from "../../../../adapters/allUsuarioSolicitanteAdapter";
import { SelectInput } from '../../../../components/selectInput/SelectInput';
import { GET_DETALLE_LIQUIDACION } from "../../../../data/graphQL/queries/PreLiquidacion/getDetalleLiquidacion";
import { formatCurrency } from "../../../../utils/mathUtils";

const initialAjuste = {
    ajuste: '',
    idUsuarioSolicitante: null,
    idMotivoAjuste: null,
    observacionAjuste: ''
};

export const DialogSolicitarAjusteLiquidacion = ({
    open,
    setOpen,
    liquidacion,
    motivos,
    ajusteExistente,
    fetchAjuste,
    limpiarAjuste,
    guardarAjuste,
    loadingMutation,
    mes,
    anio,
    porcentajeMaxMontoAjuste,
    valorMaxMontoAjusteCP,
    idTipoProfesional
}) => {
    const [ajuste, setAjuste] = useState(initialAjuste);
    const [solicitantes, setSolicitantes] = useState([]);

    // Al abrir el modal, traer el ajuste existente (si lo hay)
    useEffect(() => {
        if (!open || !liquidacion?.idLiquidacionProfesionalDetalle) return;
        fetchAjuste(liquidacion.idLiquidacionProfesionalDetalle);
    }, [open, liquidacion]);

    // Cuando llega el ajuste existente, precargar el form
    useEffect(() => {
        if (!open) return;

        if (ajusteExistente) {
            setAjuste({
                ajuste: ajusteExistente.ajuste,
                idUsuarioSolicitante: ajusteExistente.idUsuarioSolicitante ?? null,
                idMotivoAjuste: ajusteExistente.idMotivoAjuste,
                observacionAjuste: ajusteExistente.observacionAjuste ?? ''
            });
        } else {
            setAjuste(initialAjuste);
        }
    }, [ajusteExistente, open]);

    const handleChange = (name, value) => {
        setAjuste(prev => ({ ...prev, [name]: value }));
    };

    const [getUsuariosSolicitantes, { data: dataUsuariosSolicitantes, loading: solicitantesLoading }] = useLazyQuery(GET_ALL_USUARIO_SOLICITANTE, {
        fetchPolicy: 'no-cache'
    });

    const [getDetalle] = useLazyQuery(GET_DETALLE_LIQUIDACION, {
        fetchPolicy: 'no-cache'
    });

    useEffect(() => {
        if (!solicitantesLoading)
            getUsuariosSolicitantes();
    }, []);

    useEffect(() => {
        if (!dataUsuariosSolicitantes) return;
        setSolicitantes(adaptAllUsuarioSolicitanteArray(dataUsuariosSolicitantes));
    }, [dataUsuariosSolicitantes]);

    const validarPorcentajeMaxMontoAjuste = async () => {
        const { data } = await getDetalle({
            variables: {
                idEvaluacion: liquidacion?.idEvaluacion,
                idProfesional: liquidacion?.idProfesional,
                mes,
                anio,
                idEvaluacionProfesional: liquidacion?.idEvaluacionProfesional
            }
        });

        const rows = data?.detalleLiquidacion?.valorEsperado ?? [];

        if (!rows.length) {
            toast.error('Ocurrió un problema al validar el monto del ajuste.');
            return { montoMaximoAjuste: 0, esValido: false };
        }

        const totalAValidar = rows
            .filter(r => r.tipo.toLowerCase() !== 'ajuste' && r.tipo.toLowerCase() !== 'fidelizado')
            .reduce((total, r) => total + (r.total || 0), 0);

        const montoMaximoAjuste = (totalAValidar * porcentajeMaxMontoAjuste) / 100;
        const ajusteValue = parseFloat(ajuste.ajuste) || 0;
        const esValido = ajusteValue <= montoMaximoAjuste;

        return { montoMaximoAjuste, esValido };
    };

    const validar = async () => {
        if (!ajuste.ajuste || ajuste.ajuste === '-') {
            toast.error('El valor del ajuste es obligatorio');
            return false;
        }
        if (!ajuste.idUsuarioSolicitante) {
            toast.error('El solicitante es obligatorio');
            return false;
        }
        if (!ajuste.idMotivoAjuste) {
            toast.error('El motivo es obligatorio');
            return false;
        }
        if (!ajuste.observacionAjuste?.trim()) {
            toast.error('La justificación es obligatoria');
            return false;
        }

        // Validación general para todos los profesionales
        const { montoMaximoAjuste, esValido } = await validarPorcentajeMaxMontoAjuste();
        if (!esValido) {
            toast.error(`El ajuste no puede ser mayor a ${formatCurrency(montoMaximoAjuste)}`);
            return false;
        }

         const esMedico = idTipoProfesional === 1;
        // Validación especial para médicos en programas CP y CD
        if (esMedico && (liquidacion.programa === 'CP' || liquidacion.programa === 'CD')) {
            const baseLiquidacion = parseFloat(liquidacion.liquidacionTotal - liquidacion.montoAjuste) || 0;
            const totalValue = (parseFloat(ajuste.ajuste) || 0) + baseLiquidacion;
            const montoMaximoAjuste = (valorMaxMontoAjusteCP - baseLiquidacion);
            const esValidoCP = totalValue <= valorMaxMontoAjusteCP;
            if (!esValidoCP) {
                toast.error(`El ajuste no puede ser mayor a ${formatCurrency(montoMaximoAjuste)} ya que no puede superar el total de ${formatCurrency(valorMaxMontoAjusteCP)}`);
                return false;
            }
        }

        return true;
    };

    const handleClose = () => {
        setOpen(false);
        setAjuste(initialAjuste);
        limpiarAjuste();
    };

    const handleAccept = async () => {
        if (!await validar()) return;

        const ok = await guardarAjuste({
            idLiquidacionProfesionalDetalle: liquidacion.idLiquidacionProfesionalDetalle,
            ajuste: parseFloat(ajuste.ajuste),
            idUsuarioSolicitante: ajuste.idUsuarioSolicitante,
            idMotivoAjuste: ajuste.idMotivoAjuste,
            observacionAjuste: ajuste.observacionAjuste.trim()
        });

        if (ok) handleClose();
    };

    const titulo = ajusteExistente ? 'Editar Ajuste' : 'Solicitar Ajuste';

    return (
        <CustomDialog
            open={open}
            fullWidth
            boldDialogTitle
            maxWidth='sm'
            dialogTitle={titulo}
            onCancel={handleClose}
            onAccept={handleAccept}
            cancelText='Cerrar'
            acceptDisabled={loadingMutation}
        >
            <Grid container sx={{ mt: 2 }} spacing={2}>
                <Grid size={3}>
                    <RenderInputByTypeMobile
                        input={{
                            displayName: 'Ajuste',
                            name: 'ajuste',
                            type: 'currency',
                            currencyAllowNegative: true
                        }}
                        handleChange={handleChange}
                        value={ajuste.ajuste}
                    />
                </Grid>
                <Grid size={9}>
                    <SelectInput
                        options={solicitantes}
                        label='Solicitante'
                        name='idUsuarioSolicitante'
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                        value={ajuste?.idUsuarioSolicitante || ''} />
                </Grid>
                <Grid size={12}>
                    <RenderInputByTypeMobile
                        input={{
                            displayName: 'Motivo',
                            name: 'idMotivoAjuste',
                            type: 'select',
                            options: motivos
                        }}
                        handleChange={handleChange}
                        value={ajuste.idMotivoAjuste || ''}
                    />
                </Grid>
                <Grid size={12}>
                    <RenderInputByTypeMobile
                        input={{
                            displayName: 'Justificación',
                            name: 'observacionAjuste',
                            type: 'long_string'
                        }}
                        handleChange={handleChange}
                        value={ajuste.observacionAjuste}
                    />
                </Grid>
            </Grid>
        </CustomDialog>
    );
};